import { useQuery } from '@apollo/client';
import { Flex, useDisclosure } from '@chakra-ui/react';

import { CapitalDepositStatus } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import { CompanyLegalBeneficiaryStatus } from '../../legacy/Pages/BeneficiariesDocs/types';
import NextStepInfoModal from './components/NextStepInfoModal/NextStepInfoModal';
import { CapitalDepositUnderReviewQueryDocument } from './graphql/capitalDepositUnderReviewQuery.gql';
import locales from './locales';
import NextStepsAwaitingBeneficiaries from './NextStepsAwaitingBeneficiaries';
import NextStepsDocumentsReviewed from './NextStepsDocumentsReviewed';
import NextStepsFinaliseCreation from './NextStepsFinaliseCreation';
import NextStepsFundsDeposit from './NextStepsFundsDeposit';
import {
  MultiKycCapitalDepositNextStep,
  SoloKycCapitalDepositNextStep,
} from './types';

const soloKycNextSteps: SoloKycCapitalDepositNextStep[] = [
  {
    component: NextStepsDocumentsReviewed,
    inProgressStatuses: [
      CapitalDepositStatus.ApplicationSubmitted,
      CapitalDepositStatus.FormCompleted,
      CapitalDepositStatus.FundsReleased,
      CapitalDepositStatus.KycSentForPrecheck,
      CapitalDepositStatus.KycPrechecked,
      CapitalDepositStatus.KycSentToTreezor,
      CapitalDepositStatus.KycSentForPostcheck,
      CapitalDepositStatus.KycInvalid,
      CapitalDepositStatus.KycValidated,
      CapitalDepositStatus.KybSentForPrecheck,
      CapitalDepositStatus.KybPrechecked,
      CapitalDepositStatus.KybSentToTreezor,
      CapitalDepositStatus.KybInvalid,
      CapitalDepositStatus.KybValidated,
      CapitalDepositStatus.KybSentForPostcheck,
      CapitalDepositStatus.CompanyCreated,
    ],
  },
  {
    component: NextStepsFundsDeposit,
    inProgressStatuses: [
      CapitalDepositStatus.IbanstartAccountCreated,
      CapitalDepositStatus.ProviderAccountCreated,
    ],
  },
  {
    component: NextStepsFinaliseCreation,
    inProgressStatuses: [
      CapitalDepositStatus.FundsReceived,
      CapitalDepositStatus.CertificateOfDepositSent,
    ],
  },
];

const multiKycNextSteps: MultiKycCapitalDepositNextStep[] = [
  {
    getComponent: (isAwaitingBeneficiaries) =>
      isAwaitingBeneficiaries
        ? NextStepsAwaitingBeneficiaries
        : NextStepsDocumentsReviewed,
    inProgressStatuses: [
      CapitalDepositStatus.ApplicationSubmitted,
      CapitalDepositStatus.FormCompleted,
      CapitalDepositStatus.FundsReleased,
      CapitalDepositStatus.KycSentForPrecheck,
      CapitalDepositStatus.KycPrechecked,
      CapitalDepositStatus.KycSentToTreezor,
      CapitalDepositStatus.KycSentForPostcheck,
      CapitalDepositStatus.KycValidated,
      CapitalDepositStatus.KycInvalid,
      CapitalDepositStatus.KybSentForPrecheck,
      CapitalDepositStatus.KybPrechecked,
      CapitalDepositStatus.KybSentToTreezor,
      CapitalDepositStatus.KybInvalid,
      CapitalDepositStatus.KybValidated,
      CapitalDepositStatus.KybSentForPostcheck,
      CapitalDepositStatus.CompanyCreated,
    ],
  },
  {
    getComponent: () => NextStepsFundsDeposit,
    inProgressStatuses: [
      CapitalDepositStatus.IbanstartAccountCreated,
      CapitalDepositStatus.ProviderAccountCreated,
    ],
  },
  {
    getComponent: () => NextStepsFinaliseCreation,
    inProgressStatuses: [
      CapitalDepositStatus.FundsReceived,
      CapitalDepositStatus.CertificateOfDepositSent,
    ],
  },
];

const getNextStepComponent = (
  isMultiKYC: boolean | undefined,
  currentStatus: CapitalDepositStatus | undefined,
  isAwaitingBeneficiaries: boolean | undefined,
) => {
  if (currentStatus === undefined) {
    throw new Error('Capital deposit status unavailable');
  }
  if (isMultiKYC === undefined) {
    throw new Error('isMultiKYC status is unavailable');
  }
  if (isAwaitingBeneficiaries === undefined) {
    throw new Error('isAwaitingBeneficiaries status is unavailable');
  }
  if (isMultiKYC) {
    const step = multiKycNextSteps.find(({ inProgressStatuses }) =>
      inProgressStatuses.includes(currentStatus),
    );
    return step?.getComponent(isAwaitingBeneficiaries);
  }
  const step = soloKycNextSteps.find(({ inProgressStatuses }) =>
    inProgressStatuses.includes(currentStatus),
  );
  return step?.component;
};

const AWAITING_BENEFICIARY_STATUSES: CompanyLegalBeneficiaryStatus[] = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'INVALID',
];

interface CapitalDepositNextStepsProps {
  goToSmsSent: () => void;
  goToBeneficiaries: () => void;
}

const CapitalDepositNextSteps = asBentoPage<CapitalDepositNextStepsProps>(
  ({ goToBeneficiaries, goToSmsSent, Layout, onPrev }) => {
    const { companyProfileId } = useCompanyContext();
    const { isOpen, onClose, onOpen } = useDisclosure();

    const query = useQuery(CapitalDepositUnderReviewQueryDocument, {
      variables: {
        companyProfileId: companyProfileId ?? '',
      },
    });

    return (
      <Layout nav={<OBNavigation />}>
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header
            display={{ base: 'none', md: 'initial' }}
            title={locales.title}
          />

          <SuspenseQuery query={query}>
            {({ viewer: { company, companyUser, profile } }) => {
              const capitalDeposit = company?.capitalDeposit;

              if (!company || !companyUser || !capitalDeposit) {
                throw new Error(
                  'Company and companyUser should exist in CapitalDepositNextSteps',
                );
              }

              const isAwaitingBeneficiaries = company.legalBeneficiaries.some(
                ({ onboardingStatus }) =>
                  AWAITING_BENEFICIARY_STATUSES.includes(onboardingStatus),
              );

              const StepComponent = getNextStepComponent(
                company.isMultiKYC,
                capitalDeposit.status,
                isAwaitingBeneficiaries,
              );

              if (!StepComponent) {
                throw new Error('No step component found');
              }

              return (
                <>
                  <StepComponent
                    companyLegalBeneficiaries={company.legalBeneficiaries}
                    companyName={company.profile.legalName}
                    email={profile.email ?? ''}
                    isMandatee={companyUser.isMandatee ?? false}
                    onBeneficiariesClick={goToBeneficiaries}
                    onPrev={onPrev}
                    onSuccessfulSmsSend={goToSmsSent}
                    onWhatIsNextLinkPress={onOpen}
                  />

                  <NextStepInfoModal
                    capitalDepositStatus={capitalDeposit.status}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                </>
              );
            }}
          </SuspenseQuery>
        </Flex>
      </Layout>
    );
  },
);

export default CapitalDepositNextSteps;
